/**
 * Source: docs/rules/06-reputation.md
 *
 * Shared with Interceptor, like factions and rank titles: nothing here is
 * specific to captains or ships. The one Leviathan-specific piece -- the
 * Recommended Renown table, which is keyed on a captain's Cost -- lives in
 * @personnel/rules-leviathan instead, since each ruleset costs its personnel
 * on its own scale.
 *
 * Renown costs nothing. It sits outside both Cost and Experience.
 */

export const REPUTATION_PATH_IDS = ["honorable", "pragmatic", "ruthless"] as const;
export type ReputationPathId = (typeof REPUTATION_PATH_IDS)[number];

export interface ReputationPathDef {
  readonly id: ReputationPathId;
  readonly name: string;
}

export const REPUTATION_PATHS: Record<ReputationPathId, ReputationPathDef> = {
  honorable: { id: "honorable", name: "Honorable" },
  pragmatic: { id: "pragmatic", name: "Pragmatic" },
  ruthless: { id: "ruthless", name: "Ruthless" },
};

export const REPUTATION_PATH_LIST: readonly ReputationPathDef[] = REPUTATION_PATH_IDS.map(
  (id) => REPUTATION_PATHS[id],
);

export const MIN_TIER = 1;
export const MAX_TIER = 5;

/** Renown at which a captain chooses a path -- the foot of Tier 2. */
export const PATH_CHOICE_RENOWN = 11;

/**
 * Lowest Renown in each tier, highest first. The bands are NOT uniform: Tier 4
 * spans 31-50, twice the width of the others, so this has to be a table rather
 * than arithmetic on the Renown value.
 */
const TIER_FLOORS: readonly { readonly tier: number; readonly renown: number }[] = [
  { tier: 5, renown: 51 },
  { tier: 4, renown: 31 },
  { tier: 3, renown: 21 },
  { tier: 2, renown: 11 },
  { tier: 1, renown: 0 },
];

/** Reputation title by tier (index 0 is Tier 1), then by path. */
const TIER_TITLES: readonly Record<ReputationPathId, string>[] = [
  { honorable: "Unproven", pragmatic: "Unproven", ruthless: "Unproven" },
  { honorable: "Steadfast", pragmatic: "Trusted", ruthless: "Aggressive" },
  { honorable: "Gallant", pragmatic: "Reliable", ruthless: "Relentless" },
  { honorable: "Distinguished", pragmatic: "Proven", ruthless: "Infamous" },
  { honorable: "Legendary", pragmatic: "Resolute", ruthless: "Scourge" },
];

export function reputationTierFor(renown: number): number {
  const floor = TIER_FLOORS.find((entry) => renown >= entry.renown);
  return floor?.tier ?? MIN_TIER;
}

/** Lowest Renown that reaches this tier, for explaining a shortfall. */
export function renownRequiredForTier(tier: number): number {
  return TIER_FLOORS.find((entry) => entry.tier === tier)?.renown ?? 0;
}

/**
 * The captain's Reputation. Every path reads "Unproven" at Tier 1, so a path
 * is not needed there; above it, a captain who has not chosen one yet has no
 * Reputation to show.
 */
export function reputationFor(tier: number, path: ReputationPathId | null): string | null {
  const titles = TIER_TITLES[tier - 1];
  if (titles === undefined) return null;
  if (tier === MIN_TIER) return titles.honorable;
  if (path === null) return null;
  return titles[path];
}
